import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, FileText, Plus, Sparkles } from 'lucide-react'
import { useTemplateList } from '../../hooks/useTemplates'
import { useTemplatePlaceholders } from '../../hooks/useTemplatePlaceholders'
import { TemplateCard } from './TemplateCard'
import { TemplateDetail } from './TemplateDetail'
import { CreateTemplateWizard } from './CreateTemplateWizard'
import { InlineHint } from '../common/InlineHint'
import { ContextualTip } from '../common/ContextualTip'
import type { TemplateCategory } from '../../services/templatesApi'

const CATEGORIES: Array<{ value: TemplateCategory; labelKey: string }> = [
  { value: 'hr', labelKey: 'templates.categories.hr' },
  { value: 'sales', labelKey: 'templates.categories.sales' },
  { value: 'legal', labelKey: 'templates.categories.legal' },
  { value: 'real_estate', labelKey: 'templates.categories.realEstate' },
  { value: 'finance', labelKey: 'templates.categories.finance' },
  { value: 'internal', labelKey: 'templates.categories.internal' },
  { value: 'other', labelKey: 'templates.categories.other' },
]

/**
 * Browse and search templates.
 * Selecting a template opens TemplateDetail with variable form + signers + generation.
 */
interface TemplateListProps {
  organizationId?: string
}

export function TemplateList({ organizationId }: TemplateListProps = {}) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<string>('')
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const { scanPlaceholders } = useTemplatePlaceholders()
  const [detectedVars, setDetectedVars] = useState<string[]>([])

  // 7a: Auto-detect {{variables}} in the open document
  useEffect(() => {
    scanPlaceholders().then(setDetectedVars).catch(() => {})
  }, [scanPlaceholders])

  const { data, isLoading, error } = useTemplateList({
    search: search || undefined,
    category: category || undefined,
    organizationId,
  })

  // Word add-in can only use DOCX templates
  const docxTemplates = data?.filter((t) => t.content_type === 'docx') ?? []

  // Create template view
  if (isCreating) {
    return (
      <CreateTemplateWizard
        onBack={() => setIsCreating(false)}
        organizationId={organizationId}
      />
    )
  }

  // If a template is selected, show its detail view
  if (selectedTemplateId !== null) {
    return (
      <TemplateDetail
        templateId={selectedTemplateId}
        onBack={() => setSelectedTemplateId(null)}
        organizationId={organizationId}
      />
    )
  }

  return (
    <div className="flex flex-col flex-1">
      {/* Hint */}
      <div className="px-3 pt-2 space-y-2">
        <InlineHint hintKey="templates-intro">
          {t('hints.templatesIntro')}
        </InlineHint>
        <ContextualTip context="first-template">
          {t('tips.firstTemplate')}
        </ContextualTip>
      </div>

      {/* 7a: Auto-detected variables suggestion */}
      {detectedVars.length > 0 && (
        <div className="mx-3 mt-2">
          <button
            onClick={() => setIsCreating(true)}
            className="w-full flex items-start gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors text-left"
          >
            <Sparkles size={14} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                {t('templates.autoDetect.found', { count: detectedVars.length })}
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {detectedVars.slice(0, 5).map((v) => (
                  <span key={v} className="px-1.5 py-0.5 text-[9px] font-mono rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                    {`{{${v}}}`}
                  </span>
                ))}
                {detectedVars.length > 5 && (
                  <span className="text-[9px] text-emerald-500">+{detectedVars.length - 5}</span>
                )}
              </div>
              <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1">
                {t('templates.autoDetect.createHint')}
              </p>
            </div>
          </button>
        </div>
      )}

      {/* Search bar + create button */}
      <div className="px-3 py-2 space-y-1.5">
        <div className="flex gap-1.5">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('templates.searchPlaceholder')}
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1 focus:border-primary-500"
            />
          </div>
          <button
            onClick={() => setIsCreating(true)}
            className="shrink-0 px-2.5 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors"
            title={t('createTemplate.title')}
          >
            <Plus size={16} />
          </button>
        </div>

        {/* Category filter */}
        <div className="flex gap-1 overflow-x-auto scrollbar-hide">
          <CategoryChip
            label={t('templates.allCategories')}
            active={!category}
            onClick={() => setCategory('')}
          />
          {CATEGORIES.map((cat) => (
            <CategoryChip
              key={cat.value}
              label={t(cat.labelKey)}
              active={category === cat.value}
              onClick={() => setCategory(cat.value)}
            />
          ))}
        </div>
      </div>

      {/* Template list */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {isLoading && (
          <div className="text-sm text-neutral-400 text-center py-8">
            {t('common.loading')}
          </div>
        )}

        {error && (
          <div className="text-sm text-error-500 text-center py-8">
            {t('common.error')}
          </div>
        )}

        {data && docxTemplates.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FileText size={32} className="text-neutral-300 dark:text-neutral-600 mb-3" />
            <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              {t('templates.noTemplates')}
            </p>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
              {t('templates.noTemplatesDescription')}
            </p>
          </div>
        )}

        {docxTemplates.length > 0 && (
          <div className="space-y-2">
            {docxTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onSelect={() => setSelectedTemplateId(template.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function CategoryChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 px-2.5 py-1 text-[10px] rounded-full border transition-colors whitespace-nowrap ${
        active
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
          : 'border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:border-neutral-300'
      }`}
    >
      {label}
    </button>
  )
}
